/**
 * Admin → Settings → Company Research tab (E07-F02-T06, web side).
 *
 * 1:1 port of the legacy prototype `research.proto.js` renderSettings / stratRow /
 * setStrategy / setGate (../EER app/prototype/assets/research.proto.js L1257-1360;
 * MOCK.settings in research.data.js). Three panels:
 *
 *   1. Data-source strategy — a radio list of the pluggable adapters. Exactly ONE
 *      is active (green "active" badge). A strategy with `isAvailable===false` is
 *      greyed + disabled with a "no access" (apollo) / "pending" (crunchbase) badge
 *      and CANNOT be selected (parity with stratRow `disabled = !available`; the
 *      server also enforces it → STRATEGY_UNAVAILABLE). Caption verbatim:
 *      "Pluggable discovery/verification adapters. Exactly one active. Greyed = no
 *      credential."
 *   2. Review-gate mode — Mandatory specialist review (`gate`, default) vs
 *      Auto-deliver (`auto`).
 *   3. Default weight profile — READ-ONLY preview bars (research_get_settings
 *      returns weightProfile read-only; T06 acceptance #4).
 *
 * Below the grid, full width: the Dictionaries card (E07-F03-T03,
 * DictionariesCard.tsx — Industries / Sub-categories / Locations chips).
 *
 * Each change PUTs `/research/settings` (research_save_settings) and the server
 * persists it (updated_by / updated_at) + returns the authoritative state, which
 * re-renders the radios. Activating an unavailable strategy is impossible in the
 * UI (disabled) and rejected server-side — a defensive toast is shown if the
 * mutation ever errors (e.g. credential revoked → STRATEGY_UNAVAILABLE).
 *
 * Transport: researchSettingsApi.ts → the shared `apiClient`.
 */
import {
  DEFAULT_DISCOVERY_LIMITS,
  DISCOVERY_LIMIT_BOUNDS,
  type DiscoveryLimits,
  type ReviewGateMode,
  type StrategyDTO,
  type StrategyKey,
  type WeightProfileEntry,
} from '@eer/shared';
import { useEffect, useState, type JSX } from 'react';
import { useToast } from '../ui/index.js';
import { errorMessage } from '../api/client.js';
import { useResearchSettings, useSaveResearchSettings } from './researchSettingsApi.js';
import { DictionariesCard } from './DictionariesCard.js';

/** The "no access" / "pending" label for an unavailable strategy (prototype stratRow). */
function unavailableLabel(key: string): string {
  return key === 'apollo' ? 'no access' : 'pending';
}

/** One strategy radio row (prototype stratRow). */
function StrategyRow(props: {
  strategy: StrategyDTO;
  busy: boolean;
  onSelect: (key: StrategyKey) => void;
}): JSX.Element {
  const { strategy, busy, onSelect } = props;
  const active = strategy.isActive;
  const disabled = !strategy.isAvailable || busy;

  const ring = active
    ? 'border-slate-900 ring-2 ring-slate-900/10 bg-slate-50'
    : !strategy.isAvailable
      ? 'border-gray-200 bg-gray-50 opacity-60'
      : 'border-gray-200 hover:border-slate-400';

  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={active}
      onClick={() => onSelect(strategy.key as StrategyKey)}
      className={`w-full text-left flex items-start gap-3 p-3 border rounded-lg transition ${ring} ${
        disabled ? 'cursor-not-allowed' : ''
      }`}
    >
      <span className="mt-0.5" aria-hidden>
        {active ? '◉' : '○'}
      </span>
      <span className="flex-1">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-800">{strategy.name}</span>
          {!strategy.isAvailable ? (
            <span className="text-[10px] bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">
              {unavailableLabel(strategy.key)}
            </span>
          ) : active ? (
            <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
              active
            </span>
          ) : null}
        </span>
        <span className="block text-xs text-gray-400 mt-0.5">{strategy.description}</span>
      </span>
    </button>
  );
}

/** One review-gate mode card (prototype setGate buttons). */
function GateCard(props: {
  selected: boolean;
  busy: boolean;
  title: string;
  description: string;
  onSelect: () => void;
}): JSX.Element {
  const { selected, busy, title, description, onSelect } = props;
  return (
    <button
      type="button"
      disabled={busy}
      aria-pressed={selected}
      onClick={onSelect}
      className={`flex-1 p-4 rounded-lg border-2 text-left transition ${
        selected ? 'border-slate-900 bg-slate-50' : 'border-gray-200 hover:border-slate-400'
      } ${busy ? 'cursor-not-allowed opacity-70' : ''}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-bold text-slate-800">{title}</span>
        {selected ? <span aria-hidden>{'✓'}</span> : null}
      </div>
      <span className="text-xs text-gray-400">{description}</span>
    </button>
  );
}

/** One read-only weight bar (prototype weights). */
function WeightBar(props: { entry: WeightProfileEntry }): JSX.Element {
  const { entry } = props;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-600 w-40">{entry.label}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
        <div
          className="h-full bg-[#D4AF37] rounded-full"
          style={{ width: `${Math.min(100, entry.weight * 2)}%` }}
        />
      </div>
      <span className="text-xs font-bold text-slate-700 w-8 text-right">{entry.weight}%</span>
    </div>
  );
}

type LimitKey = keyof DiscoveryLimits;

/**
 * Validate one ceiling draft against DISCOVERY_LIMIT_BOUNDS (the same bounds the
 * server-side zod schema enforces). Returns the integer, or null when invalid.
 */
export function parseDiscoveryLimit(key: LimitKey, draft: string): number | null {
  const trimmed = draft.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  const { min, max } = DISCOVERY_LIMIT_BOUNDS[key];
  return n >= min && n <= max ? n : null;
}

/**
 * Discovery limits panel (E07-F02-T13) — the provider-discovery ceilings:
 * `perTile` (companies read for one tile, several provider pages) and
 * `perRequest` (distinct companies for the whole request). Saved via the same
 * PUT /research/settings; each run snapshots the values at start.
 */
function DiscoveryLimitsPanel(props: {
  limits: DiscoveryLimits;
  busy: boolean;
  onSave: (limits: DiscoveryLimits) => void;
}): JSX.Element {
  const { limits, busy, onSave } = props;
  const [draft, setDraft] = useState<Record<LimitKey, string>>({
    perTile: String(limits.perTile),
    perRequest: String(limits.perRequest),
  });
  // Re-seed the inputs when the authoritative server value changes.
  useEffect(() => {
    setDraft({ perTile: String(limits.perTile), perRequest: String(limits.perRequest) });
  }, [limits.perTile, limits.perRequest]);

  const perTile = parseDiscoveryLimit('perTile', draft.perTile);
  const perRequest = parseDiscoveryLimit('perRequest', draft.perRequest);
  const valid = perTile !== null && perRequest !== null;
  const dirty = valid && (perTile !== limits.perTile || perRequest !== limits.perRequest);

  const fields: Array<{ key: LimitKey; label: string; hint: string }> = [
    {
      key: 'perTile',
      label: 'Companies per tile',
      hint: 'Pages are read for one sub-industry × location × size tile until this many companies or the results run out.',
    },
    {
      key: 'perRequest',
      label: 'Companies per request',
      hint: 'Total distinct companies for the whole request (all tiles together).',
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm p-6" data-testid="discovery-limits">
      <h2 className="text-xl font-bold mb-1">Discovery limits</h2>
      <p className="text-xs text-gray-400 mb-4">
        Ceilings for the free company search (no AI). Defaults: {DEFAULT_DISCOVERY_LIMITS.perTile}{' '}
        per tile, {DEFAULT_DISCOVERY_LIMITS.perRequest} per request. Applies to new runs.
      </p>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (perTile !== null && perRequest !== null && dirty) onSave({ perTile, perRequest });
        }}
      >
        {fields.map((f) => {
          const bounds = DISCOVERY_LIMIT_BOUNDS[f.key];
          const invalid = parseDiscoveryLimit(f.key, draft[f.key]) === null;
          return (
            <label key={f.key} className="block">
              <span className="text-sm font-medium text-slate-800">{f.label}</span>
              <input
                type="number"
                inputMode="numeric"
                min={bounds.min}
                max={bounds.max}
                step={1}
                value={draft[f.key]}
                disabled={busy}
                aria-invalid={invalid}
                data-testid={`discovery-limit-${f.key}`}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                className={`mt-1 block w-40 border rounded px-2 py-1 text-sm ${
                  invalid ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              <span className={`block text-xs mt-1 ${invalid ? 'text-red-600' : 'text-gray-400'}`}>
                {invalid ? `Whole number from ${bounds.min} to ${bounds.max}.` : f.hint}
              </span>
            </label>
          );
        })}
        <button
          type="submit"
          disabled={busy || !dirty}
          data-testid="discovery-limits-save"
          className="px-4 py-2 rounded bg-slate-900 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save limits
        </button>
      </form>
    </div>
  );
}

/** Admin Settings → Company Research tab. */
export function ResearchSettingsTab(): JSX.Element {
  const toast = useToast();
  const settingsQuery = useResearchSettings();
  const save = useSaveResearchSettings();

  const busy = save.isPending;

  function selectStrategy(key: StrategyKey): void {
    if (settingsQuery.data?.activeStrategy === key) return; // idempotent no-op
    save.mutate(
      { activeStrategy: key },
      {
        onSuccess: () => toast.success(`Strategy → ${key}`),
        onError: (e) => toast.error(e.detail.message),
      },
    );
  }

  function selectGate(mode: ReviewGateMode): void {
    if (settingsQuery.data?.reviewGate === mode) return;
    save.mutate(
      { reviewGate: mode },
      {
        onSuccess: () =>
          toast.success(`Review gate → ${mode === 'gate' ? 'Mandatory review' : 'Auto-deliver'}`),
        onError: (e) => toast.error(e.detail.message),
      },
    );
  }

  function saveLimits(limits: DiscoveryLimits): void {
    save.mutate(
      { discoveryLimits: limits },
      {
        onSuccess: () =>
          toast.success(
            `Discovery limits → ${limits.perTile} per tile, ${limits.perRequest} per request`,
          ),
        onError: (e) => toast.error(e.detail.message),
      },
    );
  }

  if (settingsQuery.isLoading) {
    return <div className="p-6 text-sm text-gray-400">Loading…</div>;
  }
  if (settingsQuery.isError || !settingsQuery.data) {
    return (
      <div className="p-6 text-sm text-red-600">
        {errorMessage(settingsQuery.error, 'Failed to load research settings')}
      </div>
    );
  }

  const { strategies, reviewGate, weightProfile, discoveryLimits } = settingsQuery.data;
  const gateOn = reviewGate === 'gate';

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Data-source strategy */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold mb-1">Data-source strategy</h2>
          <p className="text-xs text-gray-400 mb-4">
            Pluggable discovery/verification adapters. Exactly one active. Greyed = no credential.
          </p>
          <div className="space-y-2">
            {strategies.map((s) => (
              <StrategyRow key={s.key} strategy={s} busy={busy} onSelect={selectStrategy} />
            ))}
          </div>
        </div>

        {/* Review-gate mode */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold mb-1">Review-gate mode</h2>
          <p className="text-xs text-gray-400 mb-4">
            Whether a specialist must approve the long-list before it reaches the recruiter.
          </p>
          <div className="flex gap-3 mb-6">
            <GateCard
              selected={gateOn}
              busy={busy}
              title="Mandatory specialist review"
              description="Default. Job pauses at awaiting_review; publish only after a human accepts."
              onSelect={() => selectGate('gate')}
            />
            <GateCard
              selected={!gateOn}
              busy={busy}
              title="Auto-deliver"
              description="Skip the gate — deliver the ranked list to the recruiter automatically."
              onSelect={() => selectGate('auto')}
            />
          </div>

          {/* Default weight profile — read-only preview */}
          <h3 className="text-sm font-bold mb-2 text-slate-700">Default weight profile</h3>
          <p className="text-xs text-gray-400 mb-3">
            Read-only preview of the default scoring weights.
          </p>
          <div className="space-y-2">
            {weightProfile.map((w) => (
              <WeightBar key={w.label} entry={w} />
            ))}
          </div>
        </div>

        {/* Discovery limits (E07-F02-T13) */}
        <DiscoveryLimitsPanel limits={discoveryLimits} busy={busy} onSave={saveLimits} />
      </div>

      {/* Dictionaries (E07-F03-T03) — full width below the settings grid */}
      <DictionariesCard />
    </div>
  );
}

export default ResearchSettingsTab;
